import type { AxiosResponse } from 'axios';
import axios from 'axios';
import type { GenerateMfaSettingsResponseDto, MfaSettingsDto, MfaTrustedDeviceDto } from './dto';
import type {
  EnableMfaRequestModel,
  DisableMfaRequestModel,
  GenerateMfaSettingsResponseModel,
  VerifyOtpRequestModel,
  VerifyRecoveryCodeRequestModel,
  SendVerificationEmailResponseModel,
  MarkEmailVerifiedRequestModel,
  MfaSettingsModel,
  MfaTrustedDeviceModel,
  SendVerificationEmailRequestModel,
} from './model';
import {
  mapVerifyOtpModelToDto,
  mapVerifyRecoveryCodeModelToDto,
  mapSendVerificationEmailResponseDtoToModel,
  mapGenerateMfaSettingsDtoToModel,
  mapMfaSettingsDtoToModel,
  mapMfaTrustedDeviceDtoToModel,
} from './mappers';

const getRootPath = (userId: number): string => {
  return `/api/v1/users/${userId}/mfa-settings`;
};

export const generateMfaSettings = (userId: number): Promise<GenerateMfaSettingsResponseModel> => {
  return axios
    .put<GenerateMfaSettingsResponseDto>(getRootPath(userId))
    .then((response) => mapGenerateMfaSettingsDtoToModel(response.data));
};

export const enableMfa = ({ userId, otp }: EnableMfaRequestModel): Promise<AxiosResponse> => {
  return axios.post(`${getRootPath(userId)}/enable`, { otp });
};

export const disableMfa = ({ userId }: DisableMfaRequestModel): Promise<AxiosResponse> => {
  return axios.delete(`${getRootPath(userId)}`);
};

export const getMfaSettings = (userId: number): Promise<MfaSettingsModel> => {
  return axios
    .get<MfaSettingsDto>(getRootPath(userId))
    .then((response) => mapMfaSettingsDtoToModel(response.data));
};

export const verifyOtp = ({
  mfaStateKey,
  otp,
  rememberDevice,
}: VerifyOtpRequestModel): Promise<AxiosResponse> => {
  return axios.post(
    '/api/v1/users/mfa-settings/verify-and-login',
    mapVerifyOtpModelToDto({ mfaStateKey, otp, rememberDevice }),
  );
};

export const verifyRecoveryCode = ({
  mfaStateKey,
  recoveryCode,
}: VerifyRecoveryCodeRequestModel): Promise<AxiosResponse> => {
  return axios.post(
    '/api/v1/users/mfa-settings/verify-recovery-code',
    mapVerifyRecoveryCodeModelToDto({ mfaStateKey, recoveryCode }),
  );
};

export const sendVerificationEmail = ({
  locale,
}: SendVerificationEmailRequestModel): Promise<SendVerificationEmailResponseModel> => {
  return axios
    .post('/api/v1/email-verification/send-verification-email', { locale })
    .then((response) => mapSendVerificationEmailResponseDtoToModel(response.data));
};

export const markEmailVerified = ({
  code,
  vid,
}: MarkEmailVerifiedRequestModel): Promise<AxiosResponse> => {
  return axios.post('/api/v1/email-verification/verify', { code, vid });
};

export const getMfaTrustedDevice = (userId: number): Promise<MfaTrustedDeviceModel[]> => {
  return axios
    .get<{ status: string; data: MfaTrustedDeviceDto[] }>(
      `/api/v1/users/${userId}/mfa-trusted-devices`,
    )
    .then(({ data: responsePayload }) =>
      responsePayload.data.map((item) => mapMfaTrustedDeviceDtoToModel(item)),
    );
};

export const deleteMfaTrustedDevice = (
  userId: number,
  deviceId: number,
): Promise<{ success: boolean }> => {
  return axios
    .delete<{ success: boolean }>(`/api/v1/users/${userId}/mfa-trusted-devices/${deviceId}`)
    .then((response) => response.data);
};
